import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { InitTOTPResDto, TOTPStep, VerificationMethod } from '@/interfaces/totp';
import userServices from '@/api/userServices';
import { encryptData, decryptData } from '@/utils/totpHelpers';

interface TOTPState {
  currentStep: TOTPStep;
  loading: boolean;
  error: string;
  TOTPData: InitTOTPResDto;
  backupCodes: string[];
  source: 'login' | 'login-verify' | 'profile';
  unusedBackupCodes: number;
  isRegeneration: boolean;
  authMethodUsed: VerificationMethod | undefined;
  showAuthPreferenceModal: boolean;
  challengeId: string;
  nextRetryAt: string;
  username: string;
}

const getPersistedData = (key: string, defaultValue: any) => {
  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) return defaultValue;
    return ['backupCodes', 'totpData'].includes(key) ? decryptData(stored) || defaultValue : JSON.parse(stored);
  } catch {
    return defaultValue;
  }
};

const initialState: TOTPState = {
  currentStep: TOTPStep.QR_SETUP,
  loading: false,
  error: '',
  TOTPData: getPersistedData('totpData', { qrCodeB64: '', qrCodeUri: '', secret: '' }),
  backupCodes: getPersistedData('backupCodes', []),
  source: 'login',
  unusedBackupCodes: 9,
  isRegeneration: false,
  authMethodUsed: undefined,
  showAuthPreferenceModal: false,
  challengeId: '',
  nextRetryAt: '',
  username: '',
};

export const initTOTP = createAsyncThunk('TOTP/init', async (password: string, { rejectWithValue }) => {
  try {
    return await userServices.initialiseTOTP(password);
  } catch (error: any) {
    return rejectWithValue(error.response || error);
  }
});

export const finalizeTOTP = createAsyncThunk('TOTP/finalize', async (code: string, { rejectWithValue }) => {
  try {
    return await userServices.finalizeTOTPSetup(code);
  } catch (error: any) {
    return rejectWithValue(error.response || error);
  }
});

export const fetchBackupCodes = createAsyncThunk('TOTP/fetchBackupCodes', async (_, { rejectWithValue }) => {
  try {
    return await userServices.getBackupCodesCount();
  } catch (error: any) {
    return rejectWithValue(error.response || error);
  }
});

export const regenerateBackupCodes = createAsyncThunk('TOTP/regenerateBackupCodes', async (password: string, { rejectWithValue }) => {
  try {
    return await userServices.renewBackupCodes(password);
  } catch (error: any) {
    return rejectWithValue(error.response || error);
  }
});

export const totpSlice = createSlice({
  name: 'totp',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<TOTPStep>) => {
      state.currentStep = action.payload;
    },
    setSource: (state, action: PayloadAction<'login' | 'login-verify' | 'profile'>) => {
      state.source = action.payload;
    },
    setChallengeId: (state, action: PayloadAction<string>) => {
      state.challengeId = action.payload;
    },
    setNextRetryAt: (state, action: PayloadAction<string>) => {
      state.nextRetryAt = action.payload;
    },
    setUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
    },
    setIsRegeneration: (state, action: PayloadAction<boolean>) => {
      state.isRegeneration = action.payload;
    },
    setAuthMethodUsed: (state, action: PayloadAction<VerificationMethod>) => {
      state.authMethodUsed = action.payload;
      state.showAuthPreferenceModal = true;
    },
    clearAuthPreference: state => {
      state.authMethodUsed = undefined;
      state.showAuthPreferenceModal = false;
    },
    clearTOTPState: () => {
      sessionStorage.removeItem('totpData');
      sessionStorage.removeItem('backupCodes');
      return initialState;
    },
  },
  extraReducers: builder => {
    builder.addCase(initTOTP.pending, state => {
      state.loading = true;
    });

    builder.addCase(initTOTP.fulfilled, (state, action) => {
      state.loading = false;
      state.TOTPData = action.payload;
      sessionStorage.setItem('totpData', encryptData(action.payload));
    });

    builder.addCase(initTOTP.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Error';
    });

    builder.addCase(finalizeTOTP.fulfilled, (state, action) => {
      state.loading = false;
      state.backupCodes = action.payload.backupCodes;
      state.currentStep = TOTPStep.SUCCESS;
      sessionStorage.removeItem('totpData');
      sessionStorage.setItem('backupCodes', encryptData(action.payload.backupCodes));
    });

    builder.addCase(finalizeTOTP.pending, state => {
      state.loading = true;
    });

    builder.addCase(finalizeTOTP.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Error';
    });

    builder.addCase(fetchBackupCodes.fulfilled, (state, action) => {
      state.unusedBackupCodes = action.payload.totalUsedBackupCodes;
    });

    builder.addCase(regenerateBackupCodes.pending, state => {
      state.loading = true;
    });

    builder.addCase(regenerateBackupCodes.fulfilled, (state, action) => {
      state.loading = false;
      state.backupCodes = action.payload.backupCodes;
      state.unusedBackupCodes = action.payload.backupCodes.length;
      sessionStorage.setItem('backupCodes', encryptData(action.payload.backupCodes));
    });

    builder.addCase(regenerateBackupCodes.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Error';
    });
  },
});

export const {
  setCurrentStep,
  setSource,
  setChallengeId,
  setNextRetryAt,
  setIsRegeneration,
  setAuthMethodUsed,
  clearAuthPreference,
  clearTOTPState,
  setUsername,
} = totpSlice.actions;

export const selectTOTP = (state: RootState) => state.totp;

export default totpSlice.reducer;
